import { useEffect, useState, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import errorHandler from 'src/utils/error-handler';
import http from 'src/services/httpService';
import { Spinner } from 'reactstrap';
import { Row, Col } from 'src/componentes/grid';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/message-box/confirm';
import { capitalizeFirstLetter } from 'src/utils/string';
import Alert from 'src/componentes/message-box/alert';
import useAuth from 'src/hooks/useAuth';
import Mentions from 'src/componentes/mentions';
import ComentarioItem from 'src/componentes/comentarios/ComentarioItem';
import Tabs from 'src/componentes/tabs/tabs';
import Tab from 'src/componentes/tabs/tab';
import PeriodoPorPeriodicidadeRangeLookup from 'src/lookups/PeriodoPorPeriodicidadeRangeLookup';
import 'src/paginas/comentario/style/comentario.css';

const ComentarioModal = (props) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({});
  const [comentarios, setComentarios] = useState([]);
  const [, setQuantidadeComentarios] = useState(
    props.quantidadeComentarios || 0
  );
  // Estados para aba Valores
  const [periodoSelecionado, setPeriodoSelecionado] = useState(null);
  const [comentariosItemValor, setComentariosItemValor] = useState([]);
  const [isLoadingItemValor, setIsLoadingItemValor] = useState(false);
  const [itemValorIdAtual, setItemValorIdAtual] = useState(null);
  const [itemPeriodicidade, setItemPeriodicidade] = useState(
    props.itemPeriodicidade || props.item?.periodicidade?.id || null
  );
  const ref = useRef();
  const { terms: resources } = useAuth();

  const config = props.config || {
    tipoEntidade: 1,
    entidadeId: props.entidadeId,
    endpoints: {
      obter: '/Comentario/ObterComentarios',
      salvar: '/Comentario/SalvarComentario',
      excluir: '/Comentario/ExcluirComentario',
      curtir: '/Comentario/CurtirComentario',
      responder: '/Comentario/ResponderComentario',
      obterRespostas: '/Comentario/ObterRespostas',
    },
  };

  const loadComentarios = async () => {
    try {
      setIsLoading(true);
      const response = await http.post(config.endpoints.obter, {
        tipoEntidade: config.tipoEntidade,
        entidadeId: config.entidadeId,
        _timestamp: Date.now(),
      });
      setComentarios(response.data);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadItemPeriodicidade = async () => {
    try {
      const response = await http.post('/Item/ObterFiltro', {
        id: config.entidadeId,
      });
      if (response.data?.Periodicidade) {
        setItemPeriodicidade(response.data.Periodicidade.Id);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    setForm({});
    loadComentarios();

    // Se showTabs está ativo, buscar periodicidade
    if (props.showTabs && config.entidadeId && !itemPeriodicidade) {
      loadItemPeriodicidade();
    }
  }, []);

  const loadComentariosItemValor = async (periodo, ano) => {
    if (!periodo || !ano || !config.entidadeId) return;

    try {
      setIsLoadingItemValor(true);

      // Buscar itemValorId do mapa
      const response = await http.get(
        `/Comentario/ObterComentariosItemValorPorPeriodo?itemId=${config.entidadeId}&periodo=${periodo}&ano=${ano}`
      );

      setComentariosItemValor(response.data?.comentarios || []);
      setItemValorIdAtual(response.data?.itemValorId || null);
    } catch (err) {
      errorHandler(err);
      setComentariosItemValor([]);
      setItemValorIdAtual(null);
    } finally {
      setIsLoadingItemValor(false);
    }
  };

  const handlePeriodoChange = (periodo) => {
    setPeriodoSelecionado(periodo);
    const periodoNum = periodo?.Periodo ?? periodo?.periodo;
    const anoNum = periodo?.Ano ?? periodo?.ano;
    if (periodo && periodoNum && anoNum) {
      loadComentariosItemValor(periodoNum, anoNum);
    } else {
      setComentariosItemValor([]);
    }
  };

  const incrementarQuantidade = useCallback(
    (valor) => {
      if (!valor) return;
      setQuantidadeComentarios((prev) => {
        const nova = Math.max(0, prev + valor);
        if (props.onChangeQuantidade) {
          props.onChangeQuantidade(nova);
        }
        return nova;
      });
    },
    [props.onChangeQuantidade]
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (
    tipoEntidadeOverride,
    entidadeIdOverride,
    formField = 'descricao',
    reloadCallback = null,
    useItemValorLoading = false
  ) => {
    const tipoEntidade = tipoEntidadeOverride || config.tipoEntidade;
    const entidadeId = entidadeIdOverride || config.entidadeId;
    const descricao = form[formField];

    if (!descricao) return;

    try {
      if (useItemValorLoading) {
        setIsLoadingItemValor(true);
      } else {
        setIsLoading(true);
      }

      await http.post(config.endpoints.salvar, {
        tipoEntidade,
        entidadeId,
        descricao,
      });

      enqueueSnackbar(
        intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
        {
          variant: 'success',
        }
      );

      incrementarQuantidade(1);
      handleChange(formField, '');

      if (reloadCallback) {
        reloadCallback();
      } else {
        const response = await http.post(config.endpoints.obter, {
          tipoEntidade: config.tipoEntidade,
          entidadeId: config.entidadeId,
        });
        setComentarios(response.data);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      if (useItemValorLoading) {
        setIsLoadingItemValor(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (ref.current) ref.current.close();
  };

  const handleDeleteConfirm = async (
    itemId,
    comentariosList = comentarios,
    reloadCallback = loadComentarios,
    useItemValorLoading = false
  ) => {
    try {
      if (useItemValorLoading) {
        setIsLoadingItemValor(true);
      } else {
        setIsLoading(true);
      }

      const comentarioAExcluir = comentariosList.find((c) => c.id === itemId);
      const totalExcluidos = 1 + (comentarioAExcluir?.quantidadeRespostas || 0);

      await http.post(config.endpoints.excluir, {
        comentarioId: itemId,
      });

      ContentManager.addContent(
        <Alert
          title={intl.formatMessage({ id: 'sucesso' })}
          message={intl.formatMessage({
            id: 'label.registroExcluidoComSucesso',
          })}
        />
      );

      incrementarQuantidade(-totalExcluidos);
      reloadCallback();
    } catch (err) {
      errorHandler(err);
    } finally {
      if (useItemValorLoading) {
        setIsLoadingItemValor(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleDelete = (
    itemId,
    comentariosList = comentarios,
    reloadCallback = loadComentarios,
    useItemValorLoading = false
  ) => {
    if (!itemId) return;

    ContentManager.addContent(
      <Confirm
        message={capitalizeFirstLetter(
          intl.formatMessage({ id: 'desejaRealmenteExcluir' })
        )}
        handleConfirm={() =>
          handleDeleteConfirm(
            itemId,
            comentariosList,
            reloadCallback,
            useItemValorLoading
          )
        }
      />
    );
  };

  const handleEdit = (
    item,
    tipoEntidadeOverride = null,
    entidadeIdOverride = null,
    reloadCallback = loadComentarios
  ) => {
    ContentManager.addContent(
      <EditView
        id={item.id}
        tipoEntidade={tipoEntidadeOverride || config.tipoEntidade}
        entidadeId={entidadeIdOverride || config.entidadeId}
        descricao={item.descricao}
        loadComentarios={reloadCallback}
        endpoints={config.endpoints}
      />
    );
  };

  const defaultDialogConfig = {
    title: intl.formatMessage({ id: 'inserirComentario' }),
    width: 850,
    maxHeight: 880,
    inputHeight: '9.375rem',
  };

  const dialogConfig = {
    ...defaultDialogConfig,
    ...(props.dialogConfig || {}),
  };

  const subtitle =
    dialogConfig.title && dialogConfig.title !== defaultDialogConfig.title
      ? dialogConfig.title
      : null;
  const showTabs = props.showTabs || false;

  const renderComentariosItem = () => (
    <>
      <div className="comentario-input-area">
        <Row>
          <Col md={12}>
            <div className="form-group">
              <Mentions
                value={form.descricao}
                onChange={(v) => handleChange('descricao', v)}
                style={{
                  minHeight: dialogConfig.inputHeight,
                  height: dialogConfig.inputHeight,
                  overflowY: 'auto',
                }}
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className="mb-3 d-flex justify-content-end">
        <Row>
          <Col>
            <Button
              key={2}
              color="primary"
              onClick={() => handleSave()}
              disabled={props.podeEditar}
            >
              {intl.formatMessage({ id: 'label.salvar' })}
            </Button>
            <Button
              key={1}
              onClick={handleClose}
              color="secondary"
              className="ml-2"
            >
              {intl.formatMessage({ id: 'label.cancelar' })}
            </Button>
          </Col>
        </Row>
      </div>

      {/* Lista de comentários (sempre visível) */}
      <div
        style={{ maxHeight: '29.5rem', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '10rem' }}
          >
            <Spinner color="primary" />
          </div>
        ) : (
          comentarios.map((item) => (
            <ComentarioItem
              key={item.id}
              comentario={item}
              nivel={0}
              onEdit={(comentario) => handleEdit(comentario)}
              onDelete={(id) => handleDelete(id)}
              onUpdateRespostas={loadComentarios}
              onIncrementQuantidade={incrementarQuantidade}
              endpoints={config.endpoints}
            />
          ))
        )}
      </div>
    </>
  );

  const handleSaveComentarioValor = async () => {
    if (!periodoSelecionado) return;

    // Passa -1 se itemValorIdAtual for null pro backend retornar o erro apropriado
    await handleSave(
      1, // tipoEntidade: ItemValor
      itemValorIdAtual || -1,
      'descricaoValor', // formField
      () =>
        loadComentariosItemValor(
          periodoSelecionado.periodo,
          periodoSelecionado.ano
        ), // reloadCallback
      true // useItemValorLoading
    );
  };

  const renderComentariosValores = () => {
    // Preparar datas para o lookup
    let dataInicio, dataTermino;
    if (props.dataInicio && props.dataTermino) {
      dataInicio = moment(props.dataInicio).format('YYYY-MM-DDTHH:mm:ss');
      dataTermino = moment(props.dataTermino).format('YYYY-MM-DDTHH:mm:ss');
    } else {
      const anoAtual = new Date().getFullYear();
      dataInicio = moment(new Date(anoAtual, 0, 1)).format(
        'YYYY-MM-DDTHH:mm:ss'
      );
      dataTermino = moment(new Date(anoAtual, 11, 31, 23, 59, 59)).format(
        'YYYY-MM-DDTHH:mm:ss'
      );
    }

    const dataInicioItem =
      props.item?.dataInicioItemSemFormatacao || props.item?.dataInicioItem;
    const dataTerminoItem =
      props.item?.dataTerminoItemSemFormatacao || props.item?.dataTerminoItem;
    const dataInicioItemFormatada = dataInicioItem
      ? moment(
          dataInicioItem,
          typeof dataInicioItem === 'string' ? 'DD/MM/YYYY' : undefined
        )
          .startOf('month')
          .format('YYYY-MM-DDTHH:mm:ss')
      : null;
    const dataTerminoItemFormatada = dataTerminoItem
      ? moment(
          dataTerminoItem,
          typeof dataTerminoItem === 'string' ? 'DD/MM/YYYY' : undefined
        )
          .endOf('month')
          .format('YYYY-MM-DDTHH:mm:ss')
      : null;

    return (
      <>
        <div style={{ marginBottom: '0.5rem' }}>
          <Row>
            <Col md={12}>
              <div className="form-group">
                <PeriodoPorPeriodicidadeRangeLookup
                  model={{
                    label: intl.formatMessage({ id: 'periodo' }),
                    value: periodoSelecionado,
                    requestChange: handlePeriodoChange,
                  }}
                  data={{
                    periodicidadeId: itemPeriodicidade,
                    dataInicio,
                    dataTermino,
                    dataInicioItem: dataInicioItemFormatada,
                    dataTerminoItem: dataTerminoItemFormatada,
                  }}
                  disabled={!itemPeriodicidade}
                  placeholder={intl.formatMessage({ id: 'selecioneOPeriodo' })}
                />
              </div>
            </Col>
          </Row>
        </div>

        {periodoSelecionado && (
          <>
            <div
              className="comentario-input-area"
              style={{ marginBottom: '1rem' }}
            >
              <Row>
                <Col md={12}>
                  <div className="form-group">
                    <Mentions
                      value={form.descricaoValor}
                      onChange={(v) => handleChange('descricaoValor', v)}
                      style={{
                        minHeight: dialogConfig.inputHeight,
                        height: dialogConfig.inputHeight,
                      }}
                      placeholder={intl.formatMessage({
                        id: 'inserirComentario',
                      })}
                    />
                  </div>
                </Col>
              </Row>
            </div>
            <div className="mb-3 d-flex justify-content-end">
              <Row>
                <Col>
                  <Button
                    key={2}
                    color="primary"
                    onClick={handleSaveComentarioValor}
                  >
                    {intl.formatMessage({ id: 'label.salvar' })}
                  </Button>
                  <Button
                    key={1}
                    onClick={handleClose}
                    color="secondary"
                    className="ml-2"
                  >
                    {intl.formatMessage({ id: 'label.cancelar' })}
                  </Button>
                </Col>
              </Row>
            </div>
          </>
        )}

        {/* Lista de comentários de ItemValor aba Valores*/}
        <div
          style={{
            maxHeight: '22.5rem',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {isLoadingItemValor ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '10rem' }}
            >
              <Spinner color="primary" />
            </div>
          ) : periodoSelecionado ? (
            comentariosItemValor.length > 0 ? (
              comentariosItemValor.map((item) => (
                <ComentarioItem
                  key={item.id}
                  comentario={item}
                  nivel={0}
                  onEdit={(comentario) => {
                    handleEdit(
                      comentario,
                      1, // tipoEntidade: ItemValor
                      comentario.entidadeId,
                      () =>
                        loadComentariosItemValor(
                          periodoSelecionado.periodo,
                          periodoSelecionado.ano
                        )
                    );
                  }}
                  onDelete={(id) =>
                    handleDelete(
                      id,
                      comentariosItemValor,
                      () =>
                        loadComentariosItemValor(
                          periodoSelecionado.periodo,
                          periodoSelecionado.ano
                        ),
                      true // useItemValorLoading
                    )
                  }
                  onUpdateRespostas={() => {
                    if (periodoSelecionado) {
                      loadComentariosItemValor(
                        periodoSelecionado.periodo,
                        periodoSelecionado.ano
                      );
                    }
                  }}
                  onIncrementQuantidade={incrementarQuantidade}
                  endpoints={config.endpoints}
                />
              ))
            ) : (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#6c757d',
                }}
              >
                {intl.formatMessage({ id: 'label.nenhumRegistroEncontrado' })}
              </div>
            )
          ) : null}
        </div>
      </>
    );
  };

  const renderConteudoModal = () => {
    if (showTabs) {
      return (
        <Tabs styleContent={{ paddingLeft: 0, paddingRight: 0 }}>
          <Tab label={capitalizeFirstLetter(resources.item)}>
            {renderComentariosItem()}
          </Tab>
          <Tab label={capitalizeFirstLetter(resources.valores)}>
            {renderComentariosValores()}
          </Tab>
        </Tabs>
      );
    }

    return renderComentariosItem();
  };

  return (
    <Dialog
      className="popupOpen"
      title={intl.formatMessage({ id: 'label.comentario' })}
      width={dialogConfig.width}
      maxHeight={dialogConfig.maxHeight}
      padContent
      onRequestClose={handleClose}
      mouseDownClose={false}
      ref={ref}
      styleHeader={{ paddingBottom: 0 }}
      {...props}
    >
      {subtitle && (
        <div style={{ marginBottom: '0.5rem' }}>
          <label
            style={{ fontSize: '0.70rem', color: '#6c757d', marginBottom: 0 }}
          >
            {subtitle}
          </label>
        </div>
      )}
      {renderConteudoModal()}
    </Dialog>
  );
};

const EditView = (props) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({ descricao: props.descricao });
  const ref = useRef();

  const handleClose = () => {
    if (ref.current) ref.current.close();
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await http.post(props.endpoints.salvar, {
        id: props.id,
        tipoEntidade: props.tipoEntidade,
        entidadeId: props.entidadeId,
        descricao: form.descricao,
      });
      enqueueSnackbar(
        intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
        {
          variant: 'success',
        }
      );
      props.loadComentarios();
      handleClose();
    } catch (err) {
      errorHandler(err);
    }
  };

  let actions = [
    <Button key={2} color="primary" onClick={handleSave}>
      {intl.formatMessage({ id: 'label.salvar' })}
    </Button>,
    <Button key={1} onClick={handleClose} color="secondary" className="ml-2">
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,
  ];

  return (
    <Dialog
      className="popupOpen"
      title={intl.formatMessage({ id: 'inserirComentario' })}
      actions={actions}
      width={700}
      height={505}
      padContent
      onRequestClose={handleClose}
      mouseDownClose={false}
      ref={ref}
      {...props}
    >
      {
        <div>
          <Row>
            <Col md={12}>
              <div className="form-group">
                <Mentions
                  value={form.descricao}
                  onChange={(v) => handleChange('descricao', v)}
                  style={{
                    minHeight: '150px',
                    height: '150px',
                    overflowY: 'auto',
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      }
    </Dialog>
  );
};

export default ComentarioModal;
